<template>
  <Teleport to="body">
    <Transition name="hci-fade">
      <div
        v-if="show"
        class="hci-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hci-title"
        @click.self="onClose"
      >
        <div class="hci-dialog" @click.stop>
          <header class="hci-header">
            <div>
              <h2 id="hci-title" class="hci-title">
                <i class="bi bi-upload" aria-hidden="true" />
                Import company from backup
              </h2>
              <p class="hci-subtitle">
                Restore a Data Export ZIP or Anna JSON backup as a new company — no existing company required.
              </p>
            </div>
            <button type="button" class="hci-close" aria-label="Close" :disabled="busy" @click="onClose">
              <i class="bi bi-x-lg" aria-hidden="true" />
            </button>
          </header>

          <div class="hci-stepper-wrap">
            <Stepper
              :steps="stepperSteps"
              :current-step="wizardIndex"
              :clickable="true"
              :glass="true"
              @step-click="goWizard"
            />
          </div>

          <div class="hci-body">
            <Transition name="hci-panel" mode="out-in">
              <section v-if="wizardIndex === 0" key="source" class="hci-section">
                <div class="hci-source-grid">
                  <button
                    v-for="src in sources"
                    :key="src.key"
                    type="button"
                    class="hci-source-card"
                    :class="{ 'hci-source-card--selected': selectedSource === src.key }"
                    @click="selectedSource = src.key"
                  >
                    <span v-if="src.recommended" class="hci-badge">Recommended</span>
                    <i :class="src.icon" aria-hidden="true" />
                    <strong>{{ src.label }}</strong>
                    <p>{{ src.description }}</p>
                  </button>
                </div>

                <div
                  class="hci-dropzone"
                  :class="{ 'hci-dropzone--active': dragOver, 'hci-dropzone--has-file': Boolean(file) }"
                  @dragover.prevent="dragOver = true"
                  @dragleave.prevent="dragOver = false"
                  @drop.prevent="onDrop"
                  @click="triggerFilePick"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    class="hci-dropzone__input"
                    :accept="acceptAttr"
                    @change="onFileChange"
                  >
                  <i class="bi bi-cloud-arrow-up" aria-hidden="true" />
                  <p v-if="!file" class="hci-dropzone__title">Drop a file here or click to browse</p>
                  <p v-else class="hci-dropzone__title">{{ file.name }}</p>
                  <p class="hci-dropzone__hint">
                    {{ file ? formatBytes(file.size) : `Accepts ${acceptHint}` }}
                  </p>
                </div>
                <div v-if="parseError" class="hci-alert hci-alert--error" role="alert">
                  <i class="bi bi-x-circle-fill" aria-hidden="true" />
                  <div>
                    <strong>Import not started</strong>
                    <p>{{ parseError }}</p>
                    <p v-if="blockNextStep" class="hci-alert__next">{{ blockNextStep }}</p>
                  </div>
                </div>
              </section>

              <section v-else-if="wizardIndex === 1" key="preview" class="hci-section">
                <div
                  v-if="importBlocked"
                  class="hci-alert hci-alert--error hci-alert--glass"
                  role="alert"
                >
                  <i class="bi bi-shield-exclamation" aria-hidden="true" />
                  <div>
                    <strong>Import cannot start — company already exists</strong>
                    <p>{{ blockMessage }}</p>
                    <p v-if="existingCompany" class="hci-alert__meta">
                      Existing: <strong>{{ existingCompany.name }}</strong>
                      <span v-if="existingCompany.id"> · id {{ existingCompany.id }}</span>
                      <span v-if="existingCompany.email"> · {{ existingCompany.email }}</span>
                    </p>
                    <p class="hci-alert__next">
                      {{ blockNextStep || 'Open that company → Settings → Data Import to restore datasets.' }}
                    </p>
                  </div>
                </div>
                <div
                  v-else-if="companyPreview"
                  class="hci-company-preview hci-company-preview--ok"
                >
                  <i class="bi bi-building-check" aria-hidden="true" />
                  <div>
                    <strong>Will create company</strong>
                    <p>
                      {{ companyPreview.name }}
                      <span v-if="companyPreview.email" class="hci-muted">· {{ companyPreview.email }}</span>
                    </p>
                  </div>
                </div>

                <div v-if="previewDatasets.length" class="hci-stats">
                  <div class="hci-stat">
                    <span class="hci-stat__value">{{ previewDatasets.length }}</span>
                    <span class="hci-stat__label">Datasets</span>
                  </div>
                  <div class="hci-stat">
                    <span class="hci-stat__value">{{ totalPreviewRows.toLocaleString() }}</span>
                    <span class="hci-stat__label">Rows</span>
                  </div>
                  <div class="hci-stat">
                    <span class="hci-stat__value">{{ parsedMeta.sourceType || '—' }}</span>
                    <span class="hci-stat__label">Source</span>
                  </div>
                </div>

                <EmptyState
                  v-if="!previewDatasets.length"
                  icon="📄"
                  title="No file parsed yet"
                  hint="Go back and upload a Data Export ZIP or Anna JSON backup."
                  action-label="Back to source"
                  @action="wizardIndex = 0"
                />
                <div v-else class="hci-dataset-list">
                  <div
                    v-for="ds in previewDatasets"
                    :key="ds.key"
                    class="hci-dataset-block"
                    :class="{ 'hci-dataset-block--open': expandedPreviewKey === ds.key }"
                  >
                    <div class="hci-dataset-row">
                      <span class="hci-dataset-row__body">
                        <strong>{{ ds.label }}</strong>
                        <span>{{ ds.key }} · {{ ds.category }}</span>
                      </span>
                      <span class="hci-dataset-row__count">{{ ds.rowCount.toLocaleString() }} rows</span>
                      <AppButton
                        :label="expandedPreviewKey === ds.key ? 'Hide' : 'Preview'"
                        variant="ghost"
                        @click="togglePreview(ds.key)"
                      />
                    </div>
                    <Transition name="hci-preview-expand">
                      <div v-if="expandedPreviewKey === ds.key" class="hci-preview-table-wrap">
                        <p v-if="!(ds.sampleRows || []).length" class="hci-muted">
                          No sample rows in this dataset.
                        </p>
                        <table v-else class="hci-preview-table">
                          <thead>
                            <tr>
                              <th v-for="col in sampleColumns(ds)" :key="col">{{ col }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, ri) in ds.sampleRows" :key="ri">
                              <td
                                v-for="col in sampleColumns(ds)"
                                :key="col"
                                :title="String(row[col] ?? '')"
                              >
                                {{ row[col] ?? '' }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <p v-if="(ds.sampleRows || []).length" class="hci-preview-caption">
                          Showing {{ ds.sampleRows.length }} of {{ ds.rowCount.toLocaleString() }} rows
                        </p>
                      </div>
                    </Transition>
                  </div>
                </div>
                <p v-if="parsedMeta.exportedAt" class="hci-meta">
                  Exported {{ formatDate(parsedMeta.exportedAt) }}
                </p>
              </section>

              <section v-else key="confirm" class="hci-section">
                <div v-if="importBlocked" class="hci-alert hci-alert--error" role="alert">
                  <i class="bi bi-x-circle-fill" aria-hidden="true" />
                  <div>
                    <strong>Import will not start</strong>
                    <p>{{ blockMessage }}</p>
                  </div>
                </div>
                <div v-else class="hci-confirm-box">
                  <p>
                    Create a new company from
                    <strong>{{ file?.name || 'this backup' }}</strong>
                    with
                    <strong>{{ previewDatasets.length }}</strong>
                    dataset{{ previewDatasets.length === 1 ? '' : 's' }}.
                  </p>
                  <p v-if="companyPreview" class="hci-confirm-company">
                    Company name: <strong>{{ companyPreview.name }}</strong>
                  </p>
                  <ul class="hci-checklist">
                    <li>Preview reviewed</li>
                    <li>A new company will be created for your account</li>
                    <li>History is saved under Settings → Data Import → History for the new company</li>
                    <li>Datasets import with skip-existing conflict policy</li>
                  </ul>
                </div>
              </section>
            </Transition>
          </div>

          <footer class="hci-footer">
            <div class="hci-footer__meta">
              <div>
                Step {{ wizardIndex + 1 }} of {{ stepperSteps.length }}
                <span v-if="previewDatasets.length"> · {{ previewDatasets.length }} datasets</span>
              </div>
              <p v-if="blockedAdvanceReason" class="hci-footer__blocked" role="status">
                {{ blockedAdvanceReason }}
              </p>
            </div>
            <div class="hci-footer__actions">
              <AppButton
                v-if="wizardIndex > 0"
                label="Back"
                variant="ghost"
                :disabled="busy"
                @click="wizardIndex -= 1"
              />
              <AppButton
                v-if="wizardIndex < 2"
                :label="primaryNextLabel"
                variant="primary"
                :disabled="!canAdvance || busy"
                :loading="busy && wizardIndex === 0"
                @click="advanceWizard"
              />
              <AppButton
                v-else
                label="Confirm & import"
                variant="primary"
                :disabled="!canCommit || busy"
                @click="showConfirmModal = true"
              />
            </div>
          </footer>
        </div>
      </div>
    </Transition>

    <ConfirmModal
      :show="showConfirmModal"
      title="Create company from backup?"
      :message="confirmMessage"
      confirm-text="Yes, import now"
      @confirm="onConfirmImport"
      @cancel="showConfirmModal = false"
    />

    <DataExportProgressor
      :show="progressor.show"
      mode="import"
      :steps="progressor.steps"
      :format="progressor.format"
      :title="progressor.title"
      :subtitle="progressor.subtitle"
      :completed-count="progressor.completedCount"
      :total-count="progressor.totalCount"
      :phase="progressor.phase"
      :foot-message="progressor.footMessage"
      :foot-tone="progressor.footTone"
      :ok-enabled="progressor.okEnabled"
      :ok-label="progressor.okLabel"
      :file-name="progressor.fileName"
      :summary-items="progressor.summaryItems"
      :success-ready-text="progressor.successReadyText"
      :success-stats="progressor.successStats"
      @ok="closeProgressor"
    />
  </Teleport>
</template>

<script>
import ApiService from '@/services/api.service';
import ConfirmModal from '@/components/ConfirmModal.vue';
import Stepper from '@/components/Stepper.vue';
import { AppButton, EmptyState } from '@/components/ui';
import DataExportProgressor from '@/modules/settings/components/DataExportProgressor.vue';
import './HomeCompanyImportModal.css';

export default {
  name: 'HomeCompanyImportModal',
  components: {
    ConfirmModal,
    Stepper,
    AppButton,
    EmptyState,
    DataExportProgressor
  },
  props: {
    show: { type: Boolean, default: false }
  },
  emits: ['close', 'imported'],
  data() {
    return {
      wizardIndex: 0,
      selectedSource: 'anna_zip',
      file: null,
      dragOver: false,
      previewDatasets: [],
      expandedPreviewKey: null,
      companyPreview: null,
      existingCompany: null,
      importBlocked: false,
      blockMessage: '',
      blockNextStep: '',
      parsedMeta: {},
      parseError: '',
      busy: false,
      showConfirmModal: false,
      progressTimerId: null,
      progressStepIndex: 0,
      progressor: {
        show: false,
        steps: [],
        format: 'json',
        title: 'Importing company',
        subtitle: '',
        completedCount: 0,
        totalCount: 0,
        phase: 'running',
        footMessage: '',
        footTone: 'neutral',
        okEnabled: false,
        okLabel: 'OK',
        fileName: '',
        summaryItems: [],
        successReadyText: '',
        successStats: null
      },
      sources: [
        {
          key: 'anna_zip',
          label: 'Data Export ZIP',
          icon: 'bi bi-file-earmark-zip',
          recommended: true,
          description: 'Multi-folder archive from Data Export — preferred for full company restore.'
        },
        {
          key: 'anna_json',
          label: 'Anna JSON backup',
          icon: 'bi bi-braces',
          recommended: true,
          description: 'Full-fidelity JSON backup from Data Export.'
        }
      ],
      stepperSteps: [
        { title: 'Source', description: 'Upload backup' },
        { title: 'Preview', description: 'Review data' },
        { title: 'Confirm', description: 'Create company' }
      ]
    };
  },
  computed: {
    acceptAttr() {
      return this.selectedSource === 'anna_json'
        ? '.json,application/json'
        : '.zip,application/zip';
    },
    acceptHint() {
      return this.selectedSource === 'anna_json' ? '.json (Anna backup)' : '.zip (Data Export archive)';
    },
    canAdvance() {
      if (this.wizardIndex === 0) return Boolean(this.file);
      if (this.wizardIndex === 1) {
        return this.previewDatasets.length > 0 && !this.importBlocked;
      }
      return true;
    },
    canCommit() {
      return Boolean(this.file && this.previewDatasets.length && !this.importBlocked);
    },
    primaryNextLabel() {
      if (this.wizardIndex === 0) return this.previewDatasets.length ? 'Next' : 'Parse & preview';
      return 'Next';
    },
    confirmMessage() {
      const name = this.companyPreview?.name || 'a new company';
      const file = this.file?.name || 'this backup';
      return `Create “${name}” from “${file}” and import ${this.previewDatasets.length} dataset(s)? This cannot be undone automatically.`;
    },
    progressFormat() {
      return 'json';
    },
    totalPreviewRows() {
      return this.previewDatasets.reduce((n, d) => n + (d.rowCount || 0), 0);
    },
    blockedAdvanceReason() {
      if (this.busy) return '';
      if (this.wizardIndex === 0 && !this.file) {
        return 'Upload a ZIP or JSON backup before parsing.';
      }
      if (this.importBlocked) {
        return 'Import will not start while this company already exists.';
      }
      if (this.wizardIndex === 1 && !this.previewDatasets.length) {
        return 'Parse the file first to review datasets.';
      }
      if (this.wizardIndex === 2 && !this.canCommit) {
        return 'Confirm is blocked — fix the company conflict first.';
      }
      return '';
    }
  },
  watch: {
    show(val) {
      if (val) this.resetState();
    },
    selectedSource() {
      this.file = null;
      this.previewDatasets = [];
      this.companyPreview = null;
      this.clearBlockState();
      this.parseError = '';
      this.expandedPreviewKey = null;
    }
  },
  beforeUnmount() {
    this.clearProgressTimer();
  },
  methods: {
    clearBlockState() {
      this.importBlocked = false;
      this.blockMessage = '';
      this.blockNextStep = '';
      this.existingCompany = null;
    },
    applyBlockFromResponse(data, fallbackMessage) {
      const block = data?.block || null;
      const existing = block?.existingCompany || data?.existingCompany || null;
      const message = block?.message
        || data?.message
        || fallbackMessage
        || 'Import not started.';
      this.importBlocked = Boolean(block || existing || data?.blocked);
      this.blockMessage = message;
      this.blockNextStep = block?.nextStep
        || data?.nextStep
        || (existing
          ? 'Open the existing company → Settings → Data Import to restore datasets into it.'
          : '');
      this.existingCompany = existing;
      if (this.importBlocked) this.parseError = message;
    },
    resetState() {
      this.wizardIndex = 0;
      this.selectedSource = 'anna_zip';
      this.file = null;
      this.dragOver = false;
      this.previewDatasets = [];
      this.expandedPreviewKey = null;
      this.companyPreview = null;
      this.clearBlockState();
      this.parsedMeta = {};
      this.parseError = '';
      this.busy = false;
      this.showConfirmModal = false;
      this.progressor.show = false;
      this.clearProgressTimer();
    },
    onClose() {
      if (this.busy) return;
      this.$emit('close');
    },
    formatBytes(n) {
      if (!n && n !== 0) return '';
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },
    sampleColumns(ds) {
      if (Array.isArray(ds.columns) && ds.columns.length) return ds.columns.slice(0, 8);
      const first = (ds.sampleRows || [])[0];
      return first ? Object.keys(first).slice(0, 8) : [];
    },
    togglePreview(key) {
      this.expandedPreviewKey = this.expandedPreviewKey === key ? null : key;
    },
    triggerFilePick() {
      this.$refs.fileInput?.click();
    },
    onDrop(e) {
      this.dragOver = false;
      const f = e.dataTransfer?.files?.[0];
      if (f) this.setFile(f);
    },
    onFileChange(e) {
      const f = e.target.files?.[0];
      if (f) this.setFile(f);
      e.target.value = '';
    },
    setFile(file) {
      this.file = file;
      this.previewDatasets = [];
      this.companyPreview = null;
      this.parsedMeta = {};
      this.parseError = '';
      this.clearBlockState();
      this.expandedPreviewKey = null;
    },
    goWizard(idx) {
      if (idx <= this.wizardIndex) this.wizardIndex = idx;
    },
    async advanceWizard() {
      if (this.wizardIndex === 0) {
        if (!this.previewDatasets.length) {
          await this.parsePreview();
          if (!this.previewDatasets.length) return;
        }
        this.wizardIndex = 1;
        return;
      }
      if (this.wizardIndex === 1) {
        if (this.importBlocked) return;
        this.wizardIndex = 2;
      }
    },
    buildFormData() {
      const fd = new FormData();
      fd.append('file', this.file);
      fd.append('conflictPolicy', 'skip_existing');
      return fd;
    },
    async parsePreview() {
      if (!this.file) return;
      this.busy = true;
      this.parseError = '';
      this.clearBlockState();
      try {
        const response = await ApiService.post(
          '/company/data-import/bootstrap-preview',
          this.buildFormData()
        );
        const data = response.data?.data || {};
        this.previewDatasets = data.parsed?.datasets || [];
        this.companyPreview = data.companyPreview || null;
        this.parsedMeta = {
          sourceCompanyName: data.parsed?.sourceCompanyName,
          exportedAt: data.parsed?.exportedAt,
          sourceType: data.parsed?.sourceType
        };
        if (data.blocked || data.block) {
          this.applyBlockFromResponse(data);
        }
        if (this.previewDatasets[0]) this.expandedPreviewKey = this.previewDatasets[0].key;
      } catch (err) {
        const payload = err.response?.data || {};
        this.previewDatasets = [];
        this.companyPreview = null;
        this.applyBlockFromResponse(
          {
            blocked: payload.code === 'COMPANY_ALREADY_EXISTS',
            block: payload.code === 'COMPANY_ALREADY_EXISTS'
              ? {
                message: payload.message,
                existingCompany: payload.data?.existingCompany,
                nextStep: payload.data?.nextStep
              }
              : null,
            existingCompany: payload.data?.existingCompany,
            nextStep: payload.data?.nextStep,
            message: payload.message
          },
          err.message || 'Could not parse file'
        );
        if (!this.importBlocked) {
          this.parseError = payload.message || err.message || 'Could not parse file';
        }
      } finally {
        this.busy = false;
      }
    },
    onConfirmImport() {
      this.showConfirmModal = false;
      this.runBootstrap();
    },
    async runBootstrap() {
      if (!this.canCommit) {
        this.parseError = this.blockedAdvanceReason || 'Import cannot start.';
        return;
      }
      this.busy = true;
      this.openProgressor({
        title: 'Importing company',
        subtitle: 'Creating company and writing datasets…',
        format: this.progressFormat,
        steps: this.buildImportSteps()
      });
      try {
        this.startProgressAdvance();
        const response = await ApiService.post(
          '/company/data-import/bootstrap',
          this.buildFormData()
        );
        const data = response.data?.data || {};
        const summary = data.summary || {};
        this.clearProgressTimer();
        this.finishProgressSuccess({
          readyText: response.data?.message || `Company “${data.company?.name || ''}” created`,
          summaryItems: this.mapSummaryItems(summary),
          successStats: this.mapSuccessStats(summary)
        });
        this.$emit('imported', data.company || null);
      } catch (err) {
        this.clearProgressTimer();
        const payload = err.response?.data || {};
        this.applyBlockFromResponse(
          {
            blocked: payload.code === 'COMPANY_ALREADY_EXISTS',
            block: {
              message: payload.message,
              existingCompany: payload.data?.existingCompany,
              nextStep: payload.data?.nextStep
            },
            existingCompany: payload.data?.existingCompany,
            nextStep: payload.data?.nextStep,
            message: payload.message
          },
          err.message || 'Import failed'
        );
        const message = [
          payload.message || err.message || 'Import failed',
          payload.data?.nextStep
        ].filter(Boolean).join(' — ');
        this.finishProgressError(message);
      } finally {
        this.busy = false;
      }
    },
    buildImportSteps() {
      const steps = this.previewDatasets.slice(0, 12).map((ds) => ({
        key: ds.key,
        label: ds.label || ds.key,
        detail: `${(ds.rowCount || 0).toLocaleString()} rows`,
        status: 'pending',
        isDataset: true
      }));
      steps.push({
        key: '_finalize',
        label: 'Finalize company',
        detail: 'Create company & audit log',
        status: 'pending'
      });
      return steps;
    },
    mapSummaryItems(summary) {
      return (summary?.datasets || []).map((d) => {
        const wrote = (d.createCount || 0) + (d.updateCount || 0);
        const status = d.errorCount > 0 && wrote === 0
          ? 'error'
          : (wrote === 0 ? 'skipped' : 'done');
        return {
          key: d.key,
          label: d.label,
          status,
          badge: status === 'error' ? 'Failed' : (status === 'skipped' ? 'Skipped' : 'Imported'),
          rows: d.rowCount,
          detail: [
            `Created ${d.createCount || 0}`,
            `updated ${d.updateCount || 0}`,
            `skipped ${d.skipCount || 0}`,
            d.errorCount ? `${d.errorCount} errors` : null
          ].filter(Boolean).join(' · ')
        };
      });
    },
    mapSuccessStats(summary) {
      const t = summary?.totals || {};
      return [
        { label: 'Created', value: (t.created || 0).toLocaleString() },
        { label: 'Updated', value: (t.updated || 0).toLocaleString() },
        { label: 'Skipped', value: (t.skipped || 0).toLocaleString() },
        { label: 'Errors', value: (t.errors || 0).toLocaleString() }
      ];
    },
    openProgressor({ title, subtitle, format, steps }) {
      this.clearProgressTimer();
      this.progressStepIndex = 0;
      this.progressor = {
        ...this.progressor,
        show: true,
        title,
        subtitle,
        format,
        steps: steps.map((s) => ({ ...s, status: s.status || 'pending' })),
        completedCount: 0,
        totalCount: steps.length,
        phase: 'running',
        footMessage: '',
        footTone: 'neutral',
        okEnabled: false,
        fileName: this.file?.name || '',
        summaryItems: [],
        successReadyText: '',
        successStats: null
      };
    },
    activateProgressStep(index) {
      this.progressor.steps = this.progressor.steps.map((s, i) => ({
        ...s,
        status: i < index ? 'done' : (i === index ? 'active' : 'pending')
      }));
      this.progressor.completedCount = index;
      this.progressStepIndex = index;
    },
    startProgressAdvance() {
      this.activateProgressStep(0);
      this.clearProgressTimer();
      this.progressTimerId = setInterval(() => {
        const next = Math.min(this.progressStepIndex + 1, this.progressor.steps.length - 1);
        if (next === this.progressStepIndex) return;
        this.activateProgressStep(next);
      }, 700);
    },
    clearProgressTimer() {
      if (this.progressTimerId) {
        clearInterval(this.progressTimerId);
        this.progressTimerId = null;
      }
    },
    finishProgressSuccess({ readyText, summaryItems, successStats }) {
      this.progressor.phase = 'success';
      this.progressor.okEnabled = true;
      this.progressor.okLabel = 'Done';
      this.progressor.completedCount = this.progressor.totalCount;
      this.progressor.steps = this.progressor.steps.map((s) => ({
        ...s,
        status: s.status === 'error' ? 'error' : 'done'
      }));
      this.progressor.successReadyText = readyText;
      this.progressor.summaryItems = summaryItems;
      this.progressor.successStats = successStats;
      this.progressor.subtitle = 'Review the results below';
    },
    finishProgressError(message) {
      this.progressor.phase = 'error';
      this.progressor.okEnabled = true;
      this.progressor.okLabel = 'Close';
      this.progressor.footMessage = message;
      this.progressor.footTone = 'error';
      this.progressor.subtitle = 'Import did not complete';
    },
    closeProgressor() {
      const wasSuccess = this.progressor.phase === 'success';
      this.progressor.show = false;
      this.clearProgressTimer();
      if (wasSuccess) this.$emit('close');
    }
  }
};
</script>
